/**
 * Omnichannel inbox workspace — three-pane layout (conversation rail, thread,
 * Customer 360 context) with realtime updates over WebSocket.
 *
 * Serves both `/crm/conversations` and `/crm/conversations/:id`; selecting a
 * conversation is a route change so deep links and back/forward work.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MessagesSquare, X } from 'lucide-react';
import { useCrmConversations } from '@/api/bi/crmConversations';
import { ConversationListPane, type InboxFilters } from '@/components/crm/inbox/ConversationListPane';
import { ContextPane } from '@/components/crm/inbox/ContextPane';
import { ThreadPane } from '@/components/crm/inbox/ThreadPane';
import { useConversationsRealtime } from '@/features/crm/useConversationsRealtime';
import { cn } from '@/lib/cn';

export function CrmConversationsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const selectedId = id && Number.isFinite(Number(id)) ? Number(id) : null;

  const [filters, setFilters] = useState<InboxFilters>({ q: '', channel: '', status: '' });
  const [contextOpen, setContextOpen] = useState(false);

  // One unfiltered list; filtering is client-side so tab switches are instant
  // and the realtime invalidation refreshes everything at once.
  const list = useCrmConversations();
  const conversations = useMemo(() => list.data?.results ?? [], [list.data]);
  const { connected } = useConversationsRealtime({ notifications: true });

  const selectConversation = useCallback(
    (nextId: number) => {
      navigate(`/crm/conversations/${nextId}`);
    },
    [navigate],
  );

  const selected = useMemo(
    () => conversations.find((c) => c.id === selectedId) ?? null,
    [conversations, selectedId],
  );

  // j/k keyboard navigation over the currently visible ordering.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'j' && e.key !== 'k') return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      const ordered = [...conversations].sort((a, b) => {
        const ta = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
        const tb = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
        return tb - ta;
      });
      if (ordered.length === 0) return;
      const idx = ordered.findIndex((c) => c.id === selectedId);
      const next =
        e.key === 'j'
          ? ordered[Math.min(idx + 1, ordered.length - 1)]
          : ordered[Math.max(idx - 1, 0)];
      if (next && next.id !== selectedId) selectConversation(next.id);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [conversations, selectedId, selectConversation]);

  return (
    <div className="grid h-[calc(100svh-8.5rem)] min-h-[480px] grid-cols-1 gap-3 lg:grid-cols-[320px_minmax(0,1fr)] xl:grid-cols-[320px_minmax(0,1fr)_330px]">
      {/* List rail — on mobile it is the whole screen until a thread opens */}
      <div className={cn('h-full min-h-0', selectedId ? 'hidden lg:block' : 'block')}>
        <ConversationListPane
          conversations={conversations}
          isLoading={list.isLoading}
          isError={list.isError}
          onRetry={() => void list.refetch()}
          selectedId={selectedId}
          onSelect={selectConversation}
          filters={filters}
          onFilters={setFilters}
          connected={connected}
        />
      </div>

      {/* Thread */}
      <div className={cn('h-full min-h-0', selectedId ? 'block' : 'hidden lg:block')}>
        {selectedId ? (
          <ThreadPane
            convId={selectedId}
            onBack={() => navigate('/crm/conversations')}
            onToggleContext={() => setContextOpen(true)}
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-surface/50 text-muted-foreground">
            <MessagesSquare className="h-8 w-8 opacity-40" />
            <p className="text-sm font-medium">Select a conversation</p>
            <p className="text-xs">
              Tip: use <kbd className="rounded bg-muted px-1">j</kbd> /{' '}
              <kbd className="rounded bg-muted px-1">k</kbd> to move through the list
            </p>
          </div>
        )}
      </div>

      {/* Context — inline on xl, slide-over below */}
      {selected ? (
        <>
          <div className="hidden h-full min-h-0 xl:block">
            <ContextPane conv={selected} onSelectConversation={selectConversation} />
          </div>
          {contextOpen ? (
            <div className="fixed inset-0 z-[900] xl:hidden">
              <button
                type="button"
                aria-label="Close details"
                className="absolute inset-0 bg-black/40"
                onClick={() => setContextOpen(false)}
              />
              <div className="absolute bottom-0 right-0 top-0 w-[min(92vw,360px)] bg-surface p-2 shadow-xl">
                <button
                  type="button"
                  aria-label="Close details"
                  className="absolute right-3 top-3 z-10 rounded-md bg-muted p-1"
                  onClick={() => setContextOpen(false)}
                >
                  <X className="h-4 w-4" />
                </button>
                <div className="h-full">
                  <ContextPane conv={selected} onSelectConversation={(cid) => {
                    setContextOpen(false);
                    selectConversation(cid);
                  }} />
                </div>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
